import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { LoginCredentials } from "../types/userData.types";

interface LoginCardProps {
  setErrorMessage: (errors: { email: string; password: string }) => void;
  onSubmit: (loginDetails: { email: string; password: string }) => void;
  onClose: () => void;
  emailError?: string;
  passwordError?: string;
}

export const LoginCard = ({
  emailError = "",
  passwordError = "",
  onSubmit,
  onClose,
  setErrorMessage,
}: LoginCardProps) => {
  const { t } = useTranslation();
  const [loginDetails, setLoginDetails] = useState<LoginCredentials>({
    email: "",
    password: "",
  });

  useEffect(() => {
    return () => setErrorMessage({ email: "", password: "" });
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        className="relative bg-popupLight dark:bg-popupDark shadow-lg rounded-2xl p-8 w-full max-w-md mx-4 animate-fade-in-up"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition"
        >
          <X className="w-5 h-5" />
        </button>
        <h2 className="text-2xl font-bold text-center text-primaryText dark:text-primaryTextDark mb-4">
          {t("login")}
        </h2>
        <form className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primaryText dark:text-primaryTextDark">
              {t("usernameAndEmail")}
            </label>
            <input
              type="text"
              name="email"
              value={loginDetails.email}
              onChange={(e) =>
                setLoginDetails({ ...loginDetails, email: e.target.value })
              }
              placeholder={t("enterUsernameOrEmail") ?? "Enter email"}
              className="mt-1 w-full px-4 py-2 text-primaryText focus:outline-none focus:ring-2 focus:ring-secondaryText dark:focus:ring-secondaryTextDark rounded-md shadow-sm dark:bg-gray-700 dark:text-primaryTextDark"
            />

            <span
              className={`text-sm text-red-500 ${
                emailError ? "visible" : "invisible"
              }`}
            >
              {t(emailError)}
            </span>
          </div>
          <div>
            <label className="block text-sm font-medium text-primaryText dark:text-primaryTextDark">
              {t("password")}
            </label>
            <input
              type="password"
              name="password"
              value={loginDetails.password}
              onChange={(e) =>
                setLoginDetails({ ...loginDetails, password: e.target.value })
              }
              placeholder={t("enterPassword") ?? "Enter password"}
              className="mt-1 w-full px-4 py-2 text-primaryText focus:outline-none focus:ring-2 focus:ring-secondaryText dark:focus:ring-secondaryTextDark rounded-md shadow-sm dark:bg-gray-700 dark:text-primaryTextDark"
            />
            <span
              className={`text-sm text-red-500 ${
                passwordError ? "visible" : "invisible"
              }`}
            >
              {t(passwordError)}
            </span>
          </div>
          <button
            type="button"
            onClick={() => onSubmit(loginDetails)}
            className="w-full bg-secondaryText dark:bg-secondaryTextDark text-primaryText py-2 px-4 rounded-md hover:bg-blue-700 transition"
          >
            {t("login")}
          </button>
        </form>
      </div>
    </div>
  );
};
